/**
 * Coach sign-in by redirect rather than popup.
 *
 * loginWithPopup was unreliable on iPhone and iPad: Safari opens the popup as
 * a separate window or tab, which often stayed blank, and coming back to it
 * (or opening a second one) left Auth0 without its transaction cookie —
 * "Oops!, something went wrong … we couldn't find your session".
 *
 * A redirect has neither problem, but it returns to the origin with
 * ?code=&state= on the URL, and the router's catch-all <Redirect> would move
 * to /overview and drop them before the Auth0 provider read them (a class
 * component's mount runs before the provider's effect). So the app is held
 * back while the callback is handled, and the page the coach signed in from
 * is put back in the address bar before the router first reads it.
 */

/** The same test the Auth0 SDK applies. A share link has ?code= but no state. */
export const isAuthCallback = (search: string): boolean =>
  (/[?&]code=[^&]+/.test(search) || /[?&]error=[^&]+/.test(search)) && /[?&]state=[^&]+/.test(search);

export interface LoginAppState {
  returnTo?: string;
}

/** A path on this site only: never another origin, never protocol-relative. */
export const safeReturnTo = (to: unknown): string =>
  typeof to === 'string' && to.startsWith('/') && !to.startsWith('//') ? to : '/';

export const currentPath = (): string => `${window.location.pathname}${window.location.search}`;

/** Auth0Provider's onRedirectCallback: back to where the coach pressed Log in. */
export const finishLogin = (appState?: LoginAppState): void => {
  window.history.replaceState({}, document.title, safeReturnTo(appState?.returnTo));
};
